import React, { useState, useEffect } from 'react';
import { jobListings as mockJobs } from '../mockData';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import OptimizedImage from '../components/OptimizedImage';
import { Search, MapPin, Briefcase, DollarSign } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import * as api from '../lib/api';

const JobsPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [allJobs, setAllJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadJobs(); }, []);

  const loadJobs = async () => {
    try {
      const data = await api.getJobs();
      const list = Array.isArray(data) ? data : (data.jobs || []);
      setAllJobs(list); setFilteredJobs(list);
    } catch {
      setAllJobs(mockJobs); setFilteredJobs(mockJobs);
    } finally { setLoading(false); }
  };

  const handleSearch = () => {
    let filtered = allJobs;
    if (searchTerm) filtered = filtered.filter(j => j.title?.toLowerCase().includes(searchTerm.toLowerCase()) || (j.skills || []).some(s => s.toLowerCase().includes(searchTerm.toLowerCase())));
    if (locationFilter !== 'all') filtered = filtered.filter(j => j.location === locationFilter);
    if (typeFilter !== 'all') filtered = filtered.filter(j => j.type === typeFilter);
    setFilteredJobs(filtered);
  };

  const getPayRange = (j) => j.payRange || j.pay_range || { min: 0, max: 0 };

  return (
    <div className="min-h-screen bg-[#F9FBF5] pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-8 mb-8">
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">Find Your Next AI Role</h1>
          <div className="flex flex-col md:flex-row gap-3 sm:gap-4">
            <div className="flex-1 relative"><Search className="absolute left-3 top-3 text-gray-400 w-5 h-5" /><Input placeholder="Search by job title or skills..." className="pl-10" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearch()} /></div>
            <Select value={locationFilter} onValueChange={setLocationFilter}><SelectTrigger className="w-full md:w-48"><SelectValue placeholder="Location" /></SelectTrigger><SelectContent><SelectItem value="all">All Locations</SelectItem><SelectItem value="Remote">Remote</SelectItem><SelectItem value="Hybrid">Hybrid</SelectItem><SelectItem value="Onsite">Onsite</SelectItem></SelectContent></Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}><SelectTrigger className="w-full md:w-48"><SelectValue placeholder="Job Type" /></SelectTrigger><SelectContent><SelectItem value="all">All Types</SelectItem><SelectItem value="Full-time">Full-time</SelectItem><SelectItem value="Contract">Contract</SelectItem><SelectItem value="Part-time">Part-time</SelectItem></SelectContent></Select>
            <Button onClick={handleSearch} className="bg-[#0A3900] hover:bg-[#1A5C0A]">Search</Button>
          </div>
        </div>

        <div className="mb-6"><p className="text-gray-600">Showing <span className="font-semibold">{filteredJobs.length}</span> jobs</p></div>

        {loading ? <div className="text-center py-12 text-gray-500">Loading jobs...</div> : (
          <div className="grid gap-4 sm:gap-6">
            {filteredJobs.map((job) => {
              const pay = getPayRange(job);
              return (
                <div key={job.id || job.job_id} className="bg-white rounded-xl shadow-sm p-4 sm:p-6 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/jobs/${job.id || job.job_id}`)}>
                  <div className="flex flex-col sm:flex-row items-start gap-4">
                    <OptimizedImage src={job.companyLogo || job.company_logo || `https://ui-avatars.com/api/?name=${encodeURIComponent(job.companyName || job.company_name || 'C')}&background=6366F1&color=fff`} alt="" className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg" />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">{job.title}</h3>
                      <p className="text-gray-600 mb-3">{job.companyName || job.company_name}</p>
                      <div className="flex flex-wrap gap-3 sm:gap-4 mb-3 text-sm text-gray-600">
                        <span className="flex items-center gap-1"><DollarSign className="w-4 h-4" />${pay.min}-${pay.max}/hr</span>
                        <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{job.location}</span>
                        <span className="flex items-center gap-1"><Briefcase className="w-4 h-4" />{job.type}</span>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-3">{(job.skills || []).slice(0, 4).map((s, i) => (<Badge key={i} variant="secondary">{s}</Badge>))}</div>
                      <p className="text-gray-600 text-sm line-clamp-2">{job.description}</p>
                    </div>
                    <div className="text-right flex-shrink-0 self-start">
                      <Button variant="outline" onClick={(e) => { e.stopPropagation(); navigate(`/jobs/${job.id || job.job_id}`); }}>View Details</Button>
                      <p className="text-sm text-gray-500 mt-2">{job.applicationsCount || job.applications_count || 0} applicants</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && filteredJobs.length === 0 && <div className="text-center py-12"><p className="text-gray-500 text-lg">No jobs found matching your criteria.</p></div>}
      </div>
    </div>
  );
};

export default JobsPage;
